import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { buildFinancialApiUrl } from '../utils/financial-api-url.util';

export interface B2BInvoiceItem {
  id: number;
  b2BContractItemId?: number | null;
  vehicleId?: number | null;
  plate?: string | null;
  sourceType: string;
  sourceId?: number | null;
  description?: string | null;
  reference?: string | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  days?: number | null;
  unitAmount?: number | null;
  amount: number;
}

export interface B2BInvoice {
  id: number;
  b2BContractId: number;
  contractNumber?: string | null;
  contractName?: string | null;
  companyName?: string | null;
  customerId: number;
  competenceYear: number;
  competenceMonth: number;
  category: string;
  categoryLabel: string;
  status: string;
  invoiceNumber: string;
  totalAmount: number;
  dueDate?: string | null;
  issuedAt?: string | null;
  paidAt?: string | null;
  accountsReceivableId?: number | null;
  notes?: string | null;
  createdAt: string;
  items: B2BInvoiceItem[];
}

export interface B2BContractPriceTable {
  id?: number;
  b2BContractId: number;
  reserveDailyAmount?: number | null;
  telemetryMonthlyAmount?: number | null;
  claimUseWinnerBudget: boolean;
  claimFixedAmount?: number | null;
  notes?: string | null;
}

export interface B2BBillingClosingResult {
  runId: number;
  competenceYear: number;
  competenceMonth: number;
  status: string;
  contractsProcessed: number;
  invoicesCreated: number;
  errorsCount: number;
  errorSummary?: string | null;
  contracts: Array<{
    b2BContractId: number;
    contractNumber: string;
    success: boolean;
    invoicesCreated: number;
    categoriesCreated: string[];
    error?: string | null;
  }>;
}

export interface B2BBillingPreviewLine {
  b2BContractItemId?: number | null;
  vehicleId?: number | null;
  plate?: string | null;
  sourceType: string;
  sourceId?: number | null;
  description?: string | null;
  reference?: string | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  days?: number | null;
  unitAmount?: number | null;
  amount: number;
}

export interface B2BBillingPreviewCategory {
  category: string;
  categoryLabel: string;
  lineCount: number;
  totalAmount: number;
  draftExists: boolean;
  existingDraftInvoiceId?: number | null;
  lines: B2BBillingPreviewLine[];
}

export interface B2BBillingPreviewContract {
  b2BContractId: number;
  contractNumber: string;
  contractName?: string | null;
  companyName?: string | null;
  status: string;
  totalAmount: number;
  categories: B2BBillingPreviewCategory[];
}

export interface B2BBillingMirror {
  from: string;
  to: string;
  totalAmount: number;
  lineCount: number;
  categories: Array<{
    category: string;
    categoryLabel: string;
    lineCount: number;
    totalAmount: number;
  }>;
}

export interface B2BBillingPreview {
  competenceYear: number;
  competenceMonth: number;
  periodStart: string;
  periodEnd: string;
  competenceTotal: number;
  mirrorLast30Days: B2BBillingMirror;
  contracts: B2BBillingPreviewContract[];
  eligibleItemCount?: number;
  eligibleVehicleCount?: number;
  itemsActivatedForMonthlyClosing?: number;
  diagnosticMessage?: string | null;
}

export interface B2BBillingDraftResult {
  competenceYear: number;
  competenceMonth: number;
  invoicesCreated: number;
  invoicesUpdated: number;
  invoices: B2BInvoice[];
}

export interface B2BBillingClosingBody {
  competenceYear?: number;
  competenceMonth?: number;
  periodStart?: string;
  periodEnd?: string;
  b2BContractId?: number;
  category?: string;
}

export interface B2BBillingPeriodCharge {
  id?: number;
  b2BContractId: number;
  category: string;
  periodStart: string;
  periodEnd: string;
  competenceYear?: number;
  competenceMonth?: number;
  b2BContractItemId?: number | null;
  b2BAccessoryId?: number | null;
  accessoryName?: string | null;
  vehicleId?: number | null;
  plate?: string | null;
  description?: string | null;
  quantity: number;
  unitAmount: number;
  amount: number;
}

export interface B2BBillingPeriodChargeLineInput {
  b2BContractItemId?: number | null;
  b2BAccessoryId?: number | null;
  vehicleId?: number | null;
  plate?: string | null;
  description?: string | null;
  quantity: number;
  unitAmount: number;
}

export interface B2BTelemetrySuggestion {
  b2BContractItemId?: number | null;
  itemNumber?: string | null;
  vehicleId?: number | null;
  snapshotId?: number | null;
  plate?: string | null;
  trackerType?: string | null;
  trackerId?: string | null;
  vehicleUnitValue?: number | null;
  suggestedUnitAmount: number;
  suggestedQuantity: number;
}

@Injectable({ providedIn: 'root' })
export class B2bInvoiceService {
  constructor(private http: HttpClient) {}

  listByContract(
    contractId: number,
    filters?: { year?: number; month?: number; category?: string; status?: string }
  ): Observable<B2BInvoice[]> {
    let params = new HttpParams();
    if (filters?.year) params = params.set('year', String(filters.year));
    if (filters?.month) params = params.set('month', String(filters.month));
    if (filters?.category) params = params.set('category', filters.category);
    if (filters?.status) params = params.set('status', filters.status);
    return this.http.get<B2BInvoice[]>(
      buildFinancialApiUrl(`b2b/contracts/${contractId}/invoices`),
      { params }
    );
  }

  listDrafts(year?: number, month?: number): Observable<B2BInvoice[]> {
    let params = new HttpParams();
    if (year) params = params.set('year', String(year));
    if (month) params = params.set('month', String(month));
    return this.http.get<B2BInvoice[]>(buildFinancialApiUrl('b2b/invoices/drafts'), { params });
  }

  getById(id: number): Observable<B2BInvoice> {
    return this.http.get<B2BInvoice>(buildFinancialApiUrl(`b2b/invoices/${id}`));
  }

  issue(id: number): Observable<B2BInvoice> {
    return this.http.post<B2BInvoice>(buildFinancialApiUrl(`b2b/invoices/${id}/emitir`), {});
  }

  cancel(id: number): Observable<B2BInvoice> {
    return this.http.post<B2BInvoice>(buildFinancialApiUrl(`b2b/invoices/${id}/cancelar`), {});
  }

  runClosing(body?: B2BBillingClosingBody): Observable<B2BBillingClosingResult> {
    return this.http.post<B2BBillingClosingResult>(
      buildFinancialApiUrl('b2b/billing-closing/run'),
      body || {}
    );
  }

  previewClosing(body?: B2BBillingClosingBody): Observable<B2BBillingPreview> {
    return this.http.post<B2BBillingPreview>(
      buildFinancialApiUrl('b2b/billing-closing/preview'),
      body || {}
    );
  }

  createDraft(body?: B2BBillingClosingBody): Observable<B2BBillingDraftResult> {
    return this.http.post<B2BBillingDraftResult>(
      buildFinancialApiUrl('b2b/billing-closing/draft'),
      body || {}
    );
  }

  exportInvoice(id: number, format: 'xlsx' | 'pdf'): Observable<Blob> {
    const params = new HttpParams().set('format', format);
    return this.http.get(buildFinancialApiUrl(`b2b/invoices/${id}/export`), {
      params,
      responseType: 'blob'
    });
  }

  getPriceTable(contractId: number): Observable<B2BContractPriceTable> {
    return this.http.get<B2BContractPriceTable>(
      buildFinancialApiUrl(`b2b/contracts/${contractId}/price-table`)
    );
  }

  savePriceTable(contractId: number, body: B2BContractPriceTable): Observable<B2BContractPriceTable> {
    return this.http.put<B2BContractPriceTable>(
      buildFinancialApiUrl(`b2b/contracts/${contractId}/price-table`),
      body
    );
  }

  listPeriodCharges(
    contractId: number,
    periodStart: string,
    periodEnd: string,
    category: string
  ): Observable<B2BBillingPeriodCharge[]> {
    const params = new HttpParams()
      .set('periodStart', periodStart)
      .set('periodEnd', periodEnd)
      .set('category', category);
    return this.http.get<B2BBillingPeriodCharge[]>(
      buildFinancialApiUrl(`b2b/contracts/${contractId}/billing-period-charges`),
      { params }
    );
  }

  replacePeriodCharges(
    contractId: number,
    body: {
      periodStart: string;
      periodEnd: string;
      category: string;
      lines: B2BBillingPeriodChargeLineInput[];
    }
  ): Observable<B2BBillingPeriodCharge[]> {
    return this.http.put<B2BBillingPeriodCharge[]>(
      buildFinancialApiUrl(`b2b/contracts/${contractId}/billing-period-charges`),
      body
    );
  }

  suggestTelemetry(contractId: number): Observable<B2BTelemetrySuggestion[]> {
    return this.http.get<B2BTelemetrySuggestion[]>(
      buildFinancialApiUrl(`b2b/contracts/${contractId}/billing-period-charges/telemetry-suggestions`)
    );
  }

  suggestReserve(
    contractId: number,
    periodStart: string,
    periodEnd: string
  ): Observable<B2BTelemetrySuggestion[]> {
    const params = new HttpParams()
      .set('periodStart', periodStart)
      .set('periodEnd', periodEnd);
    return this.http.get<B2BTelemetrySuggestion[]>(
      buildFinancialApiUrl(`b2b/contracts/${contractId}/billing-period-charges/reserve-suggestions`),
      { params }
    );
  }

  suggestRental(
    contractId: number,
    periodStart: string,
    periodEnd: string
  ): Observable<B2BTelemetrySuggestion[]> {
    const params = new HttpParams()
      .set('periodStart', periodStart)
      .set('periodEnd', periodEnd);
    return this.http.get<B2BTelemetrySuggestion[]>(
      buildFinancialApiUrl(`b2b/contracts/${contractId}/billing-period-charges/rental-suggestions`),
      { params }
    );
  }
}
